import { Check, X } from '@untitledui/icons';

interface RowActionButtonProps {
  /** Handler called when the button is clicked */
  onClick: () => void;
  /** Accessible label describing the action (e.g. "Approve <title>") */
  ariaLabel: string;
  /** Whether the button is disabled (e.g. during an in-flight bulk operation) */
  disabled?: boolean;
}

// Shared shell for the hover-revealed row action buttons: small bordered
// square matching the design mock (and the ApproveAllButton pill treatment).
// The solid bg is load-bearing — these buttons overlay row content on reveal.
const ROW_ACTION_BUTTON =
  'flex items-center justify-center size-[26px] rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] shadow-xs transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';

/** Hover-revealed ✓ button that approves a single action from the list. */
export function RowApproveButton({
  onClick,
  ariaLabel,
  disabled = false,
}: RowActionButtonProps) {
  return (
    <button
      type='button'
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      title='Approve'
      className={`${ROW_ACTION_BUTTON} text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] active:bg-[var(--color-bg-tertiary)]`}
    >
      <Check className='size-[14px]' strokeWidth={2.5} />
    </button>
  );
}

/** Hover-revealed ✕ button that dismisses a single action from the list. */
export function RowDismissButton({
  onClick,
  ariaLabel,
  disabled = false,
}: RowActionButtonProps) {
  return (
    <button
      type='button'
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      title='Dismiss'
      className={`${ROW_ACTION_BUTTON} text-[var(--color-text-tertiary)] hover:bg-error-50 hover:text-error-500 active:bg-error-100`}
    >
      <X className='size-[14px]' strokeWidth={2.5} />
    </button>
  );
}

interface ApproveAllButtonProps {
  /** Handler called when the button is clicked */
  onClick: () => void;
  /** Button label text */
  label?: string;
  /** Whether the button is disabled (e.g. during an in-flight approval) */
  disabled?: boolean;
}

/**
 * "Approve all" pill button used in priority group headers.
 * Matches Figma: rounded-lg, white bg, subtle border + skeuomorphic shadow.
 */
export function ApproveAllButton({
  onClick,
  label = 'Approve all',
  disabled = false,
}: ApproveAllButtonProps) {
  return (
    <button
      type='button'
      disabled={disabled}
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      className='font-medium text-[11px] leading-[20px] text-[var(--color-text-secondary)] px-[12px] py-[2px] rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] shadow-xs hover:bg-[var(--color-bg-secondary)] active:bg-[var(--color-bg-tertiary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
    >
      {label}
    </button>
  );
}

interface QuickApproveButtonProps {
  /** Handler called when the button is clicked */
  onClick: () => void;
  /** Accessible label describing the action */
  ariaLabel: string;
  /** Whether the button is disabled (e.g. during an in-flight approval) */
  disabled?: boolean;
}

/**
 * Inline checkmark button for quick-approving a single CRM-only proposal.
 * Plain tick icon matching Figma design.
 */
export function QuickApproveButton({
  onClick,
  ariaLabel,
  disabled = false,
}: QuickApproveButtonProps) {
  return (
    <button
      type='button'
      disabled={disabled}
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
      // No mt-* here. This sits in ActionRowItem's title row, which is an
      // items-center flex line: a top margin does not just nudge this button,
      // it grows the line box, and everything else on the row (title,
      // timestamp) re-centres lower — drifting out of line with the checkbox
      // and source icon in the gutter, which are pinned to the title's 20px.
      className='cursor-pointer flex-shrink-0 ml-2 w-[22px] h-[22px] flex items-center justify-center rounded-full text-[var(--color-text-tertiary)] hover:text-white hover:bg-[var(--color-text-primary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
      aria-label={ariaLabel}
    >
      <Check className='w-[14px] h-[14px]' strokeWidth={2.5} />
    </button>
  );
}
